/**
 * The superMenu data-structure is a list of all the pages
 * and sections on the site.
 *
 * It is used to generate the navbar.
 * It is also used to generate the breadcrumbs.
 * It is also used to generate the footer.
 * It is also used to generate the sitemap.
 * It is also used to generate the robots.txt.
 * It is also used to generate the manifest.json.
 * It is also used to generate the .env.local.
 *
 * Do NOT export the superMenu-data-structure from this file,
 * use getter functions from the functions.ts file instead.
 */

import { getBaseUrlForType, PageType } from "../page/types"
import { MenuItem, PrimaryMenuItem } from "./types"

// CRITICAL: We ONLY export the data-structure here for use by functions.ts!!
export const superMenu = [
  {
    key: "home",
    label: "Home",
    description:
      "Enterprise Knowledge Graphs and GenAI — Designed for Reality. Proven in Production.",
    href: "/",
    image: {
      url: "https://images.unsplash.com/photo-1659886766743-23b5312e06b1",
      width: 200,
      height: 400,
      disableOverlay: true,
    },
    subPages: [
      {
        key: "home",
        label: "Welcome",
        description: "Start your journey on agnos.ai's homepage.",
        href: "/#home",
      },
      {
        key: "problem",
        label: "The Challenge",
        description: "Understand the core challenges enterprises face today.",
        href: "/#problem",
      },
      {
        key: "solution",
        label: "The Solution",
        description:
          "agnos.ai's answer to enterprise data and knowledge challenges.",
        href: "/#solution",
      },
      {
        key: "methodology",
        label: "Our Approach",
        description:
          "Plan, Build, Run: our proven methodology for transformation.",
        href: "/#methodology",
      },
      {
        key: "data-economy",
        label: "Data Economy",
        description: "Unlock the power of your enterprise data economy.",
        href: "/#data-economy",
      },
      {
        key: "regeneration",
        label: "Regeneration",
        description:
          "Regenerate smarter, not harder: modernize your enterprise.",
        href: "/#regeneration",
      },
    ],
  } as PrimaryMenuItem,
  {
    key: "knowledge",
    label: "Knowledge",
    description:
      "Deep foundations behind agnos.ai's Enterprise Knowledge Graph and GenAI methodologies.",
    href: getBaseUrlForType(PageType.KNOWLEDGE),
  } as PrimaryMenuItem,
  {
    key: "insights",
    label: "Insights",
    description:
      "Stay updated with our latest research, articles, and expert analysis.",
    href: getBaseUrlForType(PageType.INSIGHT),
    image: {
      url: "https://images.unsplash.com/photo-1481627834876-b7833e8f5570?auto=format&fit=crop&w=400&q=80",
      width: 200,
      height: 400,
      disableOverlay: true,
    },
    subPages: [
      {
        key: "articles",
        label: "Articles",
        description: "Read our latest articles and insights",
        href: getBaseUrlForType(PageType.INSIGHT) + "/articles",
      },
      {
        key: "podcasts",
        label: "Podcasts",
        description: "Listen to our podcasts and discussions",
        href: getBaseUrlForType(PageType.INSIGHT) + "/podcasts",
      },
      {
        key: "events",
        label: "Events",
        description: "Join our upcoming events and webinars",
        href: getBaseUrlForType(PageType.INSIGHT) + "/events",
      },
      {
        key: "use-cases",
        label: "Use Cases",
        description: "See how our solutions are used in real-world scenarios",
        href: getBaseUrlForType(PageType.INSIGHT) + "/use-cases",
      },
    ] as MenuItem[],
  } as PrimaryMenuItem,
  {
    key: "services",
    label: "Services",
    description: "Discover how we can help transform your business with AI.",
    href: "/services",
    image: {
      url: "https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&w=400&q=80",
      width: 200,
      height: 400,
      disableOverlay: true,
    },
    subPages: [
      {
        key: "plan-build-run",
        label: "Our Plan-Build-Run™ Methodology",
        description:
          "Our structured, multi-phase approach for long-term transformation.",
        href: "/services/plan-build-run",
      },
      {
        key: "on-demand-expertise",
        label: "Flexible, On-Demand Services",
        description: "Custom, modular support tailored to your specific needs.",
        href: "/services/on-demand-expertise",
      },
      {
        key: "training-enablement",
        label: "Training & Knowledge Transfer",
        description:
          "Build lasting internal expertise in semantic technologies",
        href: "/services/training-enablement",
      },
      {
        key: "ongoing-support",
        label: "Ongoing Support & Maintenance",
        description: "Ongoing support and maintenance",
        href: "/services/ongoing-support",
      },
    ],
  } as PrimaryMenuItem,
  {
    key: "about",
    label: "About",
    description:
      "Learn about our mission, values, and the team behind agnos.ai.",
    href: "/about",
    image: {
      url: "https://images.unsplash.com/photo-1582213782179-e0d53f98f2ca?auto=format&fit=crop&w=400&q=80",
      width: 200,
      height: 400,
    },
    subPages: [
      {
        key: "partners",
        label: "Partners",
        description:
          "Meet our trusted business partners who help us deliver enterprise intelligence solutions through knowledge graphs and generative AI.",
        href: getBaseUrlForType(PageType.PARTNER),
      },
      {
        key: "team",
        label: "Team",
        description: "Get to know our team",
        href: getBaseUrlForType(PageType.TEAM),
      },
    ],
  } as PrimaryMenuItem,
  {
    key: "contact",
    label: "Contact",
    description:
      "Ready to transform your enterprise with knowledge graphs and GenAI? Get in touch with our team.",
    href: "/contact",
    image: {
      url: "https://images.unsplash.com/photo-1586769852836-bc069f19e1b6?auto=format&fit=crop&w=400&q=80",
      width: 200,
      height: 400,
    },
  } as PrimaryMenuItem,
]
